import React from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Button,
  Alert,
  Avatar,
  Divider
} from '@mui/material';
import { 
  Block as BlockIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  ContactSupport as ContactIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface UnauthorizedUserProps {
  email?: string;
  errorType?: 'unauthorized' | 'deactivated';
}

const UnauthorizedUser: React.FC<UnauthorizedUserProps> = ({ email, errorType = 'unauthorized' }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/login');
  };

  const isDeactivated = errorType === 'deactivated';

  return (
    <Container 
      component="main" 
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 }
      }}
    >
      <Paper 
        elevation={8}
        sx={{ 
          p: { xs: 3, sm: 4 },
          width: '100%',
          borderRadius: 3,
          textAlign: 'center'
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              bgcolor: 'error.main',
              mx: 'auto',
              mb: 2
            }}
          >
            <BlockIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          </Avatar>
        </Box>

        {/* Title */}
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: 'error.main',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            mb: 2
          }}
        >
          {isDeactivated ? 'Cont Dezactivat' : 'Acces Refuzat'}
        </Typography>

        {/* Message */}
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            lineHeight: 1.6
          }}
        >
          {isDeactivated 
            ? 'Contul tău a fost dezactivat. Te rugăm să contactezi un administrator pentru a restabili accesul.'
            : 'Ne pare rău, nu ai permisiunea de a accesa această aplicație.'
          }
        </Typography>


        <Divider sx={{ my: 3 }} />

        {/* Information */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            {isDeactivated 
              ? 'Contul tău a fost temporar dezactivat. Te rugăm să contactezi administratorul pentru a restabili accesul.'
              : 'Această aplicație este restricționată doar pentru utilizatorii autorizați. Dacă crezi că ar trebui să ai acces, te rugăm să contactezi administratorul.'
            }
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ContactIcon fontSize="small" color="action" />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              Contactează administratorul pentru asistență
            </Typography>
          </Box>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          startIcon={<HomeIcon />}
          onClick={handleGoHome}
          size="large"
          sx={{
            py: { xs: 1.5, sm: 2 },
            px: { xs: 3, sm: 4 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium'
          }}
        >
          Înapoi la Autentificare
        </Button>
      </Paper>
    </Container>
  );
};

export default UnauthorizedUser;
